import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import type {
  BorderDetection,
  BorderEdge,
  DamageBox,
  DamageDetection,
  GradeShareData,
  GradeSideAnalysis,
} from '../lib/types'
import { formatCenteringPair } from '../lib/format'
import { ReactComponent as ArrowDownIconSvg } from '../../assets/icons/arrow-down.svg'
import { ReactComponent as AppCenteringIcon } from '../../assets/icons/centering.svg'
import { ReactComponent as AppDamageIcon } from '../../assets/icons/damage.svg'
import {
  getAppliedImageRectification,
  scaleImageRectificationSourceShape,
  transformImageBoxForRectifiedDisplay,
} from './slab/gradeImageRectification'
import styles from './report.module.css'

const CENTERING_MIN = 0.45
const CENTERING_MAX = 0.55

type Side = 'front' | 'back'
type IssueType = 'damage' | 'centering'
type Axis = 'tb' | 'lr'
type IssueImageMode = 'raw' | 'emboss'
type Crop = { x: number; y: number; width: number; height: number }
type LayoutRect = { left: number; top: number; width: number; height: number }
type Point = { x: number; y: number }
type Segment = { p1: Point; p2: Point }
type BorderBandOverlay = { outer: Segment; inner: Segment; fillPoints: string }

const AXIS_EDGES: Record<Axis, readonly BorderEdge[]> = {
  tb: ['top', 'bottom'],
  lr: ['left', 'right'],
}

export function GradeAnalysisCard({
  data,
  embedded = false,
}: {
  data: GradeShareData
  embedded?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const [activeIssue, setActiveIssue] = useState<IssueType | null>(null)
  const [selectedIssue, setSelectedIssue] = useState<SelectedIssue | null>(null)
  const damageSides = (['front', 'back'] as const).map((side) => ({
    key: side,
    title: side === 'front' ? 'Front' : 'Back',
    issueCount: damageIssueCount(data, side),
    detections: data.analysis?.[side]?.damageDetections ?? [],
    imageUrl: imageUrlForSide(data, side),
    analysis: data.analysis?.[side],
    rectified:
      side === 'front'
        ? Boolean(data.card.frontImageRectificationApplied)
        : Boolean(data.card.backImageRectificationApplied),
  }))
  const centeringSides = useMemo(() => centeringSideSummaries(data), [data])
  const damageItems = damageSides.flatMap((side) =>
    side.detections.map((detection, index) => ({
      key: detection.id || `${side.key}-${index}`,
      side: side.key,
      sideTitle: side.title,
      detection,
      imageUrl: side.imageUrl,
      analysis: side.analysis,
      rectified: side.rectified,
      detections: side.detections,
      index,
    })),
  )
  const centeringItems = centeringSides.flatMap((side) => side.issues)
  const damageCount = damageSides.reduce((sum, side) => sum + side.issueCount, 0)
  const centeringCount = centeringSides.reduce((sum, side) => sum + side.issueCount, 0)

  const content = (
    <>
      <div className={styles.analysisSectionGroup}>
        <h2 className={styles.analysisSectionTitle}>Issues</h2>
        <IssueRow
          icon={<DamageIcon />}
          title="Damage"
          subtitle={`${damageCount} issue${damageCount === 1 ? '' : 's'} found`}
          count={damageCount}
          active={activeIssue === 'damage'}
          onClick={() => setActiveIssue((value) => (value === 'damage' ? null : 'damage'))}
          preview={<DamagePreview count={damageCount} items={damageItems} />}
        />
        {activeIssue === 'damage' && damageCount > 0 ? (
          <IssueDetails title="Damage issues">
            {damageItems.length > 0 ? (
              damageItems.map((item) => (
                <IssueDetailRow
                  key={item.key}
                  thumbnail={
                    <DamageThumbnail
                      detection={item.detection}
                      imageUrl={item.imageUrl}
                      analysis={item.analysis}
                      rectified={item.rectified}
                    />
                  }
                  title={`${item.sideTitle} damage`}
                  meta={damageMeta(item.detection, item.index)}
                  onClick={() => setSelectedIssue({ kind: 'damage', item })}
                />
              ))
            ) : (
              <p className={styles.analysisDetailEmpty}>
                Damage was counted, but this grade does not include saved damage thumbnails.
              </p>
            )}
          </IssueDetails>
        ) : null}
        <IssueRow
          icon={<CenteringIcon />}
          title="Centering"
          subtitle={`${centeringCount} issue${centeringCount === 1 ? '' : 's'} found`}
          count={centeringCount}
          active={activeIssue === 'centering'}
          onClick={() => setActiveIssue((value) => (value === 'centering' ? null : 'centering'))}
          preview={<CenteringPreview issues={centeringItems} />}
        />
        {activeIssue === 'centering' && centeringCount > 0 ? (
          <IssueDetails title="Centering issues">
            {centeringItems.map((item) => (
              <IssueDetailRow
                key={item.key}
                thumbnail={<CenteringIssueThumbnail issue={item} />}
                title={item.title}
                meta={item.value}
                onClick={item.items.length > 0 ? () => setSelectedIssue({ kind: 'centering', item }) : undefined}
              />
            ))}
          </IssueDetails>
        ) : null}
      </div>

      <div className={styles.analysisDivider} />

      <button
        className={styles.analysisReportToggle}
        onClick={() => setExpanded((value) => !value)}
        type="button"
      >
        <span>{expanded ? 'Hide full report' : 'Show full report'}</span>
        <ArrowDownIconSvg
          className={expanded ? styles.analysisToggleIconOpen : styles.analysisToggleIcon}
          width={18}
          height={18}
          aria-hidden
        />
      </button>

      {expanded && (
        <div className={styles.analysisFullReport}>
          <div className={styles.analysisFullReportSection}>
            <div className={styles.analysisFullReportHeading}>
              <CenteringIcon size={18} />
              <span>Centering</span>
            </div>
            <div className={styles.analysisSideRow}>
              {centeringSides.map((side) => (
                <div key={side.key} className={styles.analysisMetricCard}>
                  <span className={styles.analysisReportSideLabel}>{side.title}</span>
                  <MetricRow label="T/B" value={side.topBottom} issue={side.topBottomIsIssue} />
                  <MetricRow label="L/R" value={side.leftRight} issue={side.leftRightIsIssue} />
                </div>
              ))}
            </div>
          </div>

          <div className={styles.analysisFullReportSection}>
            <div className={styles.analysisFullReportHeading}>
              <DamageIcon size={18} />
              <span>Damage counts</span>
            </div>
            <div className={styles.analysisSideRow}>
              {damageSides.map((side) => (
                <div
                  key={side.key}
                  className={`${styles.analysisMetricCard} ${styles.analysisDamageCard}`}
                >
                  <span className={styles.analysisReportSideLabel}>{side.title}</span>
                  <span
                    className={`${styles.analysisDamageCount} ${
                      side.issueCount > 0 ? styles.negative : styles.positive
                    }`}
                  >
                    {side.issueCount}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedIssue ? (
        <IssueModal issue={selectedIssue} onClose={() => setSelectedIssue(null)} />
      ) : null}
    </>
  )

  if (embedded) {
    return <div className={styles.analysisEmbedded}>{content}</div>
  }

  return (
    <section className={styles.analysisCard} aria-label="Grade analysis">
      {content}
    </section>
  )
}

function IssueRow({
  icon,
  title,
  subtitle,
  count,
  active,
  onClick,
  preview,
}: {
  icon: ReactNode
  title: string
  subtitle: string
  count: number
  active: boolean
  onClick: () => void
  preview: ReactNode
}) {
  return (
    <button
      className={`${styles.analysisIssueRow} ${count === 0 ? styles.analysisIssueRowDisabled : ''}`}
      disabled={count === 0}
      onClick={onClick}
      type="button"
      aria-expanded={count > 0 ? active : undefined}
    >
      <span className={styles.analysisIssueIcon}>{icon}</span>
      <span className={styles.analysisIssueText}>
        <span className={styles.analysisIssueTitle}>{title}</span>
        <span className={styles.analysisIssueSubtitle}>{subtitle}</span>
      </span>
      {preview}
      {count > 0 ? <span className={`${styles.analysisArrow} ${active ? styles.analysisArrowOpen : ''}`}>›</span> : null}
    </button>
  )
}

function MetricRow({ label, value, issue }: { label: string; value: string; issue: boolean }) {
  return (
    <div className={styles.analysisMetricRow}>
      <span className={styles.analysisMetricLabel}>{label}</span>
      <span className={issue ? styles.analysisMetricValueIssue : styles.analysisMetricValue}>
        {value}
      </span>
    </div>
  )
}

function DamagePreview({
  count,
  items,
}: {
  count: number
  items: Array<{
    detection: DamageDetection
    imageUrl: string
    analysis?: GradeSideAnalysis
    rectified: boolean
  }>
}) {
  const visible = Math.min(count, 3)
  if (visible === 0) return <span className={styles.analysisPreviewEmpty}>0</span>
  return (
    <span className={styles.analysisIssuePreviewRow}>
      {items.slice(0, visible).map((item, index) => (
        <span key={item.detection.id || index} className={styles.analysisIssuePreviewTile}>
          <DamageThumbnail
            detection={item.detection}
            imageUrl={item.imageUrl}
            analysis={item.analysis}
            rectified={item.rectified}
            compact
          />
        </span>
      ))}
      {items.length === 0
        ? Array.from({ length: visible }).map((_, index) => (
            <span key={index} className={styles.analysisIssuePreviewTile} />
          ))
        : null}
      {count > 3 ? <span className={styles.analysisIssuePreviewMore}>+{count - 3}</span> : null}
    </span>
  )
}

function CenteringPreview({
  issues,
}: {
  issues: CenteringIssue[]
}) {
  const issue = issues.find((candidate) => candidate.items.length > 0)
  if (!issue) return <span className={styles.analysisPreviewEmpty}>{issues.length || 0}</span>
  return (
    <span className={styles.analysisIssuePreviewRow}>
      <span className={styles.centeringMini} aria-hidden>
        <CenteringIssueThumbnail issue={issue} compact />
      </span>
      {issues.length > 1 ? (
        <span className={styles.analysisIssuePreviewMore}>+{issues.length - 1}</span>
      ) : null}
    </span>
  )
}

function IssueDetails({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className={styles.analysisIssueDetails}>
      <span className={styles.analysisDetailTitle}>{title}</span>
      <div className={styles.analysisIssueList}>{children}</div>
    </div>
  )
}

function IssueDetailRow({
  thumbnail,
  title,
  meta,
  onClick,
}: {
  thumbnail: ReactNode
  title: string
  meta: string
  onClick?: () => void
}) {
  const content = (
    <>
      <span className={styles.analysisDetailThumb}>{thumbnail}</span>
      <span className={styles.analysisDetailText}>
        <span className={styles.analysisDetailLabel}>{title}</span>
        <span className={styles.analysisDetailMeta}>{meta}</span>
      </span>
      {onClick ? <span className={styles.analysisDetailArrow}>›</span> : null}
    </>
  )

  if (onClick) {
    return (
      <button className={styles.analysisDetailRow} onClick={onClick} type="button">
        {content}
      </button>
    )
  }

  return (
    <div className={styles.analysisDetailRow}>
      {content}
    </div>
  )
}

function DamageThumbnail({
  detection,
  imageUrl,
  analysis,
  rectified = false,
  compact = false,
  mode = 'raw',
  showOverlay = true,
  detections,
}: {
  detection: DamageDetection
  imageUrl: string
  analysis?: GradeSideAnalysis
  rectified?: boolean
  compact?: boolean
  mode?: IssueImageMode
  showOverlay?: boolean
  detections?: DamageDetection[]
}) {
  const [ref, size] = useElementSize<HTMLSpanElement>()
  const cropImageUrl = damageCropImageUrl(detection, mode)
  if (cropImageUrl) {
    const cropBox = detection.crop_box
    const content =
      cropBox && size.width > 0 && size.height > 0
        ? getCoverContentRect(
            Math.max(1, cropBox.x2 - cropBox.x1),
            Math.max(1, cropBox.y2 - cropBox.y1),
            size.width,
            size.height,
          )
        : null
    const overlayDetections = detection.crop_box
      ? visibleDamageDetections(detection, detections)
      : []
    return (
      <span ref={ref} className={styles.analysisCropThumb}>
        <img
          className={styles.analysisCropAssetImage}
          src={cropImageUrl}
          alt=""
          aria-hidden
          style={
            content
              ? {
                  left: content.left,
                  top: content.top,
                  right: 'auto',
                  bottom: 'auto',
                  width: content.width,
                  height: content.height,
                  objectFit: 'fill',
                }
              : undefined
          }
        />
        {showOverlay && detection.crop_box && content
          ? overlayDetections.map((candidate, index) => (
              <DamageBoxOverlay
                key={candidate.id || `${candidate.side}-${index}`}
                box={candidate.box}
                cropBox={detection.crop_box as DamageBox}
                content={content}
                compact={compact}
                muted={!isSameDamageDetection(candidate, detection)}
              />
            ))
          : null}
      </span>
    )
  }

  let sourceWidth = positive(analysis?.sourceWidth) || 1
  let sourceHeight = positive(analysis?.sourceHeight) || 1
  let box = detection.box
  if (rectified) {
    const imageRectification = getAppliedImageRectification(analysis?.centeringProjection)
    if (imageRectification) {
      const maxX = Math.max(Math.abs(box.x1), Math.abs(box.x2))
      const maxY = Math.max(Math.abs(box.y1), Math.abs(box.y2))
      const usesDamageCoordinates =
        (maxX <= 1.5 && maxY <= 1.5) ||
        (maxX <= sourceWidth * 1.5 && maxY <= sourceHeight * 1.5)
      const damageRectification = usesDamageCoordinates
        ? scaleImageRectificationSourceShape(imageRectification, [sourceHeight, sourceWidth])
        : imageRectification
      const coordinateWidth = usesDamageCoordinates
        ? sourceWidth
        : imageRectification.source_shape[1]
      const coordinateHeight = usesDamageCoordinates
        ? sourceHeight
        : imageRectification.source_shape[0]
      const normalizedBox = normalizeDamageBox(box, coordinateWidth, coordinateHeight)
      const transformedBox = damageRectification
        ? transformImageBoxForRectifiedDisplay(normalizedBox, damageRectification)
        : null
      if (transformedBox) {
        box = transformedBox
        sourceWidth = imageRectification.display_shape[1]
        sourceHeight = imageRectification.display_shape[0]
      }
    }
  }
  const crop = squareCropAroundBox(box, sourceWidth, sourceHeight)
  return (
    <span className={styles.analysisCropThumb}>
      <CroppedImage imageUrl={imageUrl} sourceWidth={sourceWidth} sourceHeight={sourceHeight} crop={crop} />
      {showOverlay ? <span className={compact ? styles.damageThumbMarkerCompact : styles.damageThumbMarker} /> : null}
    </span>
  )
}

function CenteringIssueThumbnail({
  issue,
  compact = false,
}: {
  issue: CenteringIssue
  compact?: boolean
}) {
  return (
    <CenteringCropPair issue={issue} compact={compact} />
  )
}

function DamageBoxOverlay({
  box,
  cropBox,
  content,
  compact,
  muted = false,
}: {
  box: DamageBox
  cropBox: DamageBox
  content: LayoutRect
  compact: boolean
  muted?: boolean
}) {
  const style = damageBoxOverlayStyle(box, cropBox, content, compact ? 10 : 14)
  if (!style) return null
  return (
    <span
      className={`${compact ? styles.damageThumbBoxCompact : styles.damageThumbBox} ${
        muted ? styles.damageThumbBoxMuted : ''
      }`}
      style={style}
    />
  )
}

function CenteringCropPair({
  issue,
  compact = false,
  large = false,
  mode = 'raw',
  showOverlay = true,
}: {
  issue: CenteringIssue
  compact?: boolean
  large?: boolean
  mode?: IssueImageMode
  showOverlay?: boolean
}) {
  if (issue.items.length === 0) {
    return (
      <span className={`${styles.centeringCropPair} ${compact ? styles.centeringCropPairCompact : ''}`}>
        <span className={styles.centeringCropPlaceholder}>
          <CenteringIcon size={large ? 28 : 18} />
        </span>
      </span>
    )
  }

  return (
    <span
      className={`${styles.centeringCropPair} ${
        issue.axis === 'lr' ? styles.centeringCropPairRow : ''
      } ${compact ? styles.centeringCropPairCompact : ''} ${large ? styles.centeringCropPairLarge : ''}`}
    >
      {issue.items.map((item) => (
        <CenteringCropSegment
          key={item.detection.edge}
          item={item}
          large={large}
          compact={compact}
          mode={mode}
          showOverlay={showOverlay}
        />
      ))}
    </span>
  )
}

function CenteringCropSegment({
  item,
  large,
  compact,
  mode,
  showOverlay,
}: {
  item: CenteringIssueItem
  large: boolean
  compact: boolean
  mode: IssueImageMode
  showOverlay: boolean
}) {
  const [ref, size] = useElementSize<HTMLSpanElement>()
  const detection = item.detection
  const cropBox = detection.crop_box
  const imageUrl = borderCropImageUrl(detection, mode)
  const content = cropBox && size.width > 0 && size.height > 0
    ? getBorderDetectionCoverRect(detection, size.width, size.height)
    : null

  return (
    <span ref={ref} className={styles.centeringCropSegment}>
      {imageUrl && content ? (
        <img
          className={styles.centeringCropImage}
          src={imageUrl}
          alt=""
          aria-hidden
          style={{
            left: content.left,
            top: content.top,
            width: content.width,
            height: content.height,
          }}
        />
      ) : imageUrl ? (
        <img className={styles.analysisCropAssetImage} src={imageUrl} alt="" aria-hidden />
      ) : null}
      {content && showOverlay ? (
        <BorderDetectionOverlay
          detection={detection}
          ratioLabel={item.ratioLabel}
          content={content}
          large={large}
        />
      ) : (
        showOverlay ? <span className={`${styles.centeringEdgeBand} ${styles[`centeringEdgeBand_${detection.edge}`]}`} /> : null
      )}
    </span>
  )
}

function BorderDetectionOverlay({
  detection,
  ratioLabel,
  content,
  large,
}: {
  detection: BorderDetection
  ratioLabel?: string
  content: LayoutRect
  large: boolean
}) {
  const band = getBorderDetectionBandOverlay(detection)
  const cropBox = detection.crop_box
  if (!band || !cropBox) return null
  const cropWidth = Math.max(1, cropBox.x2 - cropBox.x1)
  const cropHeight = Math.max(1, cropBox.y2 - cropBox.y1)

  return (
    <>
      <svg
        className={styles.centeringBandOverlay}
        style={{
          left: content.left,
          top: content.top,
          width: content.width,
          height: content.height,
        }}
        viewBox={`0 0 ${cropWidth} ${cropHeight}`}
        preserveAspectRatio="none"
        aria-hidden
      >
        <polygon points={band.fillPoints} fill="rgba(255, 140, 0, 0.25)" />
        <line
          x1={band.outer.p1.x}
          y1={band.outer.p1.y}
          x2={band.outer.p2.x}
          y2={band.outer.p2.y}
          stroke="rgba(255, 140, 0, 1)"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <line
          x1={band.inner.p1.x}
          y1={band.inner.p1.y}
          x2={band.inner.p2.x}
          y2={band.inner.p2.y}
          stroke="rgba(255, 140, 0, 1)"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
      {ratioLabel ? (
        <span className={large ? styles.borderRatioPillLarge : styles.borderRatioPill}>
          {ratioLabel}
        </span>
      ) : null}
    </>
  )
}

function CroppedImage({
  imageUrl,
  sourceWidth,
  sourceHeight,
  crop,
}: {
  imageUrl: string
  sourceWidth: number
  sourceHeight: number
  crop: Crop
}) {
  const style = {
    left: `${(-crop.x / crop.width) * 100}%`,
    top: `${(-crop.y / crop.height) * 100}%`,
    width: `${(sourceWidth / crop.width) * 100}%`,
    height: `${(sourceHeight / crop.height) * 100}%`,
  } satisfies CSSProperties

  return <img className={styles.analysisCropImage} src={imageUrl} alt="" aria-hidden style={style} />
}

type DamageItem = {
  key: string
  side: Side
  sideTitle: string
  detection: DamageDetection
  imageUrl: string
  analysis?: GradeSideAnalysis
  rectified: boolean
  detections: DamageDetection[]
  index: number
}

type SelectedIssue =
  | { kind: 'damage'; item: DamageItem }
  | { kind: 'centering'; item: CenteringIssue }

function IssueModal({
  issue,
  onClose,
}: {
  issue: SelectedIssue
  onClose: () => void
}) {
  const [mode, setMode] = useState<IssueImageMode>('raw')
  const [showOverlay, setShowOverlay] = useState(true)
  const title =
    issue.kind === 'damage'
      ? `${issue.item.sideTitle} damage`
      : issue.item.title
  const meta =
    issue.kind === 'damage'
      ? damageMeta(issue.item.detection, issue.item.index)
      : issue.item.value
  const hasSurfaceView =
    issue.kind === 'damage'
      ? Boolean(issue.item.detection.emboss_file_url)
      : issue.item.items.some((item) => Boolean(item.detection.emboss_file_url))

  useEffect(() => {
    setMode('raw')
    setShowOverlay(true)
  }, [issue])

  return (
    <div className={styles.issueModalBackdrop} role="presentation" onClick={onClose}>
      <div
        className={styles.issueModal}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <button className={styles.issueModalClose} onClick={onClose} type="button" aria-label="Close">
          x
        </button>
        <div className={styles.issueModalHeader}>
          <h3 className={styles.issueModalTitle}>{title}</h3>
          <p className={styles.issueModalMeta}>{meta}</p>
        </div>
        <div className={styles.issueModalControls}>
          <button
            className={styles.issueOverlayToggle}
            type="button"
            onClick={() => setShowOverlay((value) => !value)}
          >
            {showOverlay ? 'Hide overlay' : 'Show overlay'}
          </button>
          {hasSurfaceView ? (
            <SurfaceViewButton
              active={mode === 'emboss'}
              onClick={() => setMode((value) => (value === 'emboss' ? 'raw' : 'emboss'))}
            />
          ) : (
            <span />
          )}
        </div>
        <div
          className={`${styles.issueModalFrame} ${
            issue.kind === 'centering' ? styles.issueModalFrameCentering : ''
          }`}
        >
          {issue.kind === 'damage' ? (
            <DamageThumbnail
              detection={issue.item.detection}
              imageUrl={issue.item.imageUrl}
              analysis={issue.item.analysis}
              rectified={issue.item.rectified}
              detections={issue.item.detections}
              mode={mode}
              showOverlay={showOverlay}
            />
          ) : (
            <span className={styles.centeringModalCrops}>
              <CenteringCropPair issue={issue.item} large mode={mode} showOverlay={showOverlay} />
            </span>
          )}
        </div>
        <div className={styles.issueModalMetaRows}>
          {issue.kind === 'damage' ? (
            <>
              <IssueMetaRow label="Position" value={damagePosition(issue.item.detection)} />
              <IssueMetaRow
                label="Area"
                value={
                  typeof issue.item.detection.area === 'number' && Number.isFinite(issue.item.detection.area)
                    ? `${issue.item.detection.area.toFixed(4)}%`
                    : 'Unknown'
                }
              />
              {issue.item.detection.is_corner ? <IssueMetaRow label="Location" value="Corner" /> : null}
            </>
          ) : (
            <>
              <IssueMetaRow label="Position" value={issue.item.title} />
              <IssueMetaRow label="Centering" value={issue.item.value} />
              {Number.isFinite(issue.item.items[0]?.detection.centering_error) ? (
                <IssueMetaRow
                  label="Centering error"
                  value={`${formatNumber(issue.item.items[0]?.detection.centering_error ?? 0)}% from 50%`}
                />
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function SurfaceViewButton({
  active,
  onClick,
}: {
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      className={`${styles.issueSurfaceButton} ${active ? styles.issueSurfaceButtonActive : ''}`}
      type="button"
      aria-pressed={active}
      onClick={onClick}
    >
      Surface view
      <span className={styles.issueSurfaceChip}>{active ? 'On' : 'Off'}</span>
    </button>
  )
}

function IssueMetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.issueMetaRow}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

function DamageIcon({ size = 24 }: { size?: number }) {
  return <AppDamageIcon width={size} height={size} aria-hidden />
}

function CenteringIcon({ size = 24 }: { size?: number }) {
  return <AppCenteringIcon width={size} height={size} aria-hidden />
}

function damageIssueCount(data: GradeShareData, side: Side) {
  const analysis = data.analysis?.[side]
  return Math.max(
    analysis?.damageDetections?.length ?? 0,
    analysis?.damageMaskPaths?.length ?? 0,
    data.imperfections[side].count,
  )
}

type CenteringSideSummary = {
  key: Side
  title: string
  issueCount: number
  topBottomIsIssue: boolean
  leftRightIsIssue: boolean
  topBottom: string
  leftRight: string
  issues: CenteringIssue[]
}

type CenteringIssue = {
  key: string
  side: Side
  axis: Axis
  title: string
  value: string
  items: CenteringIssueItem[]
}

type CenteringIssueItem = {
  detection: BorderDetection
  ratioLabel?: string
}

function centeringSideSummaries(data: GradeShareData): CenteringSideSummary[] {
  return (['front', 'back'] as const).map((side) => {
    const values = data.centering[side]
    const borderErrors = data.analysis?.[side]?.borderErrorSides ?? []
    const topBottomIsIssue =
      outOfBounds(values.top) || outOfBounds(values.bottom) || hasAny(borderErrors, ['top', 'bottom'])
    const leftRightIsIssue =
      outOfBounds(values.left) || outOfBounds(values.right) || hasAny(borderErrors, ['left', 'right'])
    const title = side === 'front' ? 'Front' : 'Back'
    const topBottom = formatCenteringPair(values.top, values.bottom)
    const leftRight = formatCenteringPair(values.left, values.right)
    const analysis = data.analysis?.[side]
    const visibleBorderDetections = (analysis?.borderDetections ?? []).filter(
      (detection) =>
        (!detection.side || detection.side === side) &&
        isBorderDetectionOutsideCenteringBounds(values, detection),
    )
    const issues: CenteringIssue[] = []

    if (topBottomIsIssue) {
      issues.push({
        key: `${side}-tb`,
        side,
        axis: 'tb',
        title: `${title} T/B`,
        value: topBottom,
        items: centeringIssueItems(values, visibleBorderDetections, 'tb'),
      })
    }
    if (leftRightIsIssue) {
      issues.push({
        key: `${side}-lr`,
        side,
        axis: 'lr',
        title: `${title} L/R`,
        value: leftRight,
        items: centeringIssueItems(values, visibleBorderDetections, 'lr'),
      })
    }

    return {
      key: side,
      title,
      issueCount: issues.length,
      topBottomIsIssue,
      leftRightIsIssue,
      topBottom,
      leftRight,
      issues,
    }
  })
}

function imageUrlForSide(data: GradeShareData, side: Side) {
  return side === 'front' ? data.card.frontImageUrl : data.card.backImageUrl
}

function damageCropImageUrl(detection: DamageDetection, mode: IssueImageMode = 'raw') {
  return mode === 'emboss'
    ? firstNonEmpty(detection.emboss_file_url, detection.raw_file_url, detection.thumbnail_file_url)
    : firstNonEmpty(detection.raw_file_url, detection.thumbnail_file_url, detection.emboss_file_url)
}

function borderCropImageUrl(detection: BorderDetection, mode: IssueImageMode = 'raw') {
  return mode === 'emboss'
    ? firstNonEmpty(detection.emboss_file_url, detection.raw_file_url, detection.thumbnail_file_url)
    : firstNonEmpty(detection.raw_file_url, detection.thumbnail_file_url, detection.emboss_file_url)
}

function firstNonEmpty(...values: Array<string | undefined>) {
  return values.find((value) => typeof value === 'string' && value.trim().length > 0)
}

function centeringIssueItems(
  values: GradeShareData['centering'][Side],
  detections: BorderDetection[],
  axis: Axis,
): CenteringIssueItem[] {
  return AXIS_EDGES[axis]
    .map((edge) => detections.find((detection) => detection.edge === edge))
    .filter((detection): detection is BorderDetection => Boolean(detection))
    .map((detection) => ({
      detection,
      ratioLabel: formatBorderDetectionRatio(getGradeBorderRatio(values, detection)),
    }))
}

function validCenteringRatio(value?: number | null) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null
}

function getGradeBorderRatio(values: GradeShareData['centering'][Side], detection: BorderDetection) {
  const fallbackRatio = validCenteringRatio(detection.centering_ratio)
  if (detection.edge === 'left') return validCenteringRatio(values.left) ?? fallbackRatio
  if (detection.edge === 'right') return validCenteringRatio(values.right) ?? fallbackRatio
  if (detection.edge === 'top') return validCenteringRatio(values.top) ?? fallbackRatio
  return validCenteringRatio(values.bottom) ?? fallbackRatio
}

function isBorderDetectionOutsideCenteringBounds(
  values: GradeShareData['centering'][Side],
  detection: BorderDetection,
) {
  const ratio = getGradeBorderRatio(values, detection)
  if (typeof ratio === 'number') return outOfBounds(ratio)
  return (
    typeof detection.centering_error === 'number' &&
    Number.isFinite(detection.centering_error) &&
    Math.abs(detection.centering_error) > 5
  )
}

function formatBorderDetectionRatio(value?: number | null) {
  return typeof value === 'number' && Number.isFinite(value)
    ? `${(value * 100).toFixed(1)}%`
    : undefined
}

function visibleDamageDetections(
  detection: DamageDetection,
  detections?: DamageDetection[],
) {
  if (!detection.crop_box) return []
  return (detections?.length ? detections : [detection]).filter(
    (candidate) =>
      (!candidate.side || !detection.side || candidate.side === detection.side) &&
      doDamageBoxesOverlap(candidate.box, detection.crop_box as DamageBox),
  )
}

function doDamageBoxesOverlap(a: DamageBox, b: DamageBox) {
  return a.x1 < b.x2 && a.x2 > b.x1 && a.y1 < b.y2 && a.y2 > b.y1
}

function isSameDamageDetection(a: DamageDetection, b: DamageDetection) {
  if (a.id && b.id) return a.id === b.id
  return (
    a.side === b.side &&
    a.box.x1 === b.box.x1 &&
    a.box.y1 === b.box.y1 &&
    a.box.x2 === b.box.x2 &&
    a.box.y2 === b.box.y2
  )
}

function damagePosition(detection: DamageDetection) {
  return `${Math.round(detection.box.x1)}, ${Math.round(detection.box.y1)} - ${Math.round(
    detection.box.x2,
  )}, ${Math.round(detection.box.y2)}`
}

function formatNumber(value: number, digits = 2) {
  return Number.isFinite(value) ? value.toFixed(digits) : '0.00'
}

function damageBoxOverlayStyle(
  box: DamageBox,
  cropBox: DamageBox,
  content: LayoutRect,
  minSize: number,
): CSSProperties | null {
  const cropWidth = cropBox.x2 - cropBox.x1
  const cropHeight = cropBox.y2 - cropBox.y1
  if (cropWidth <= 0 || cropHeight <= 0) return null

  const left = clamp((box.x1 - cropBox.x1) / cropWidth, 0, 1)
  const top = clamp((box.y1 - cropBox.y1) / cropHeight, 0, 1)
  const right = clamp((box.x2 - cropBox.x1) / cropWidth, 0, 1)
  const bottom = clamp((box.y2 - cropBox.y1) / cropHeight, 0, 1)
  const width = Math.max(0, (right - left) * content.width)
  const height = Math.max(0, (bottom - top) * content.height)
  const expandedWidth = Math.min(content.width, Math.max(width, minSize))
  const expandedHeight = Math.min(content.height, Math.max(height, minSize))

  return {
    left: clamp(
      content.left + left * content.width - (expandedWidth - width) / 2,
      content.left,
      content.left + content.width - expandedWidth,
    ),
    top: clamp(
      content.top + top * content.height - (expandedHeight - height) / 2,
      content.top,
      content.top + content.height - expandedHeight,
    ),
    width: expandedWidth,
    height: expandedHeight,
  }
}

function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T | null>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const element = ref.current
    if (!element) return undefined

    const update = () => {
      const rect = element.getBoundingClientRect()
      setSize((current) =>
        current.width === rect.width && current.height === rect.height
          ? current
          : { width: rect.width, height: rect.height },
      )
    }
    update()

    const observer = new ResizeObserver(update)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return [ref, size] as const
}

function getBorderDetectionAngle(detection: BorderDetection) {
  return typeof detection.angle === 'number' && Number.isFinite(detection.angle)
    ? detection.angle
    : typeof detection.angle_degrees === 'number' && Number.isFinite(detection.angle_degrees)
      ? detection.angle_degrees
      : 0
}

function getBorderDetectionBox(detection: BorderDetection): DamageBox | null {
  if (detection.box) return detection.box
  if (!detection.line) return null

  const width = Math.max(1, detection.width_pixels || 0)
  const centerX = (detection.line.x1 + detection.line.x2) / 2
  const centerY = (detection.line.y1 + detection.line.y2) / 2

  if (detection.edge === 'left' || detection.edge === 'right') {
    return {
      x1: centerX - width / 2,
      y1: Math.min(detection.line.y1, detection.line.y2),
      x2: centerX + width / 2,
      y2: Math.max(detection.line.y1, detection.line.y2),
    }
  }

  return {
    x1: Math.min(detection.line.x1, detection.line.x2),
    y1: centerY - width / 2,
    x2: Math.max(detection.line.x1, detection.line.x2),
    y2: centerY + width / 2,
  }
}

function rotatePoint(point: Point, center: Point, degrees: number): Point {
  const radians = (degrees * Math.PI) / 180
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)
  const dx = point.x - center.x
  const dy = point.y - center.y
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  }
}

function rotateSegment(segment: Segment, degrees: number): Segment {
  const center = {
    x: (segment.p1.x + segment.p2.x) / 2,
    y: (segment.p1.y + segment.p2.y) / 2,
  }
  return {
    p1: rotatePoint(segment.p1, center, degrees),
    p2: rotatePoint(segment.p2, center, degrees),
  }
}

function getBorderDetectionBandOverlay(detection: BorderDetection): BorderBandOverlay | null {
  const borderBox = getBorderDetectionBox(detection)
  const cropBox = detection.crop_box
  if (!borderBox || !cropBox) return null

  const cropWidth = cropBox.x2 - cropBox.x1
  const cropHeight = cropBox.y2 - cropBox.y1
  if (cropWidth <= 0 || cropHeight <= 0) return null

  const angle = getBorderDetectionAngle(detection)
  const toCropPoint = (point: Point) => ({
    x: clamp(point.x - cropBox.x1, 0, cropWidth),
    y: clamp(point.y - cropBox.y1, 0, cropHeight),
  })
  const toCropSegment = (segment: Segment) => ({
    p1: toCropPoint(segment.p1),
    p2: toCropPoint(segment.p2),
  })

  let outerLine: Segment
  let innerLine: Segment
  if (detection.edge === 'left') {
    outerLine = { p1: { x: borderBox.x1, y: borderBox.y1 }, p2: { x: borderBox.x1, y: borderBox.y2 } }
    innerLine = { p1: { x: borderBox.x2, y: borderBox.y1 }, p2: { x: borderBox.x2, y: borderBox.y2 } }
  } else if (detection.edge === 'right') {
    innerLine = { p1: { x: borderBox.x1, y: borderBox.y1 }, p2: { x: borderBox.x1, y: borderBox.y2 } }
    outerLine = { p1: { x: borderBox.x2, y: borderBox.y1 }, p2: { x: borderBox.x2, y: borderBox.y2 } }
  } else if (detection.edge === 'top') {
    outerLine = { p1: { x: borderBox.x1, y: borderBox.y1 }, p2: { x: borderBox.x2, y: borderBox.y1 } }
    innerLine = { p1: { x: borderBox.x1, y: borderBox.y2 }, p2: { x: borderBox.x2, y: borderBox.y2 } }
  } else {
    innerLine = { p1: { x: borderBox.x1, y: borderBox.y1 }, p2: { x: borderBox.x2, y: borderBox.y1 } }
    outerLine = { p1: { x: borderBox.x1, y: borderBox.y2 }, p2: { x: borderBox.x2, y: borderBox.y2 } }
  }

  const outer = toCropSegment(rotateSegment(outerLine, angle))
  const inner = toCropSegment(rotateSegment(innerLine, angle))
  const fillPathPoints =
    detection.edge === 'right' || detection.edge === 'bottom'
      ? [inner.p1, inner.p2, outer.p2, outer.p1]
      : [outer.p1, outer.p2, inner.p2, inner.p1]

  return {
    outer,
    inner,
    fillPoints: fillPathPoints.map((point) => `${point.x},${point.y}`).join(' '),
  }
}

function getCoverContentRect(
  cropWidth: number,
  cropHeight: number,
  width: number,
  height: number,
): LayoutRect {
  const scale = Math.max(width / cropWidth, height / cropHeight)
  const displayWidth = cropWidth * scale
  const displayHeight = cropHeight * scale
  return {
    left: (width - displayWidth) / 2,
    top: (height - displayHeight) / 2,
    width: displayWidth,
    height: displayHeight,
  }
}

function getBorderDetectionCoverRect(
  detection: BorderDetection,
  width: number,
  height: number,
): LayoutRect {
  const cropBox = detection.crop_box
  if (!cropBox) return { left: 0, top: 0, width, height }

  const cropWidth = Math.max(1, cropBox.x2 - cropBox.x1)
  const cropHeight = Math.max(1, cropBox.y2 - cropBox.y1)
  const rect = getCoverContentRect(cropWidth, cropHeight, width, height)
  const band = getBorderDetectionBandOverlay(detection)
  if (!band) return rect

  const scale = rect.width / cropWidth
  const xValues = [band.outer.p1.x, band.outer.p2.x, band.inner.p1.x, band.inner.p2.x].map(
    (value) => value * scale,
  )
  const yValues = [band.outer.p1.y, band.outer.p2.y, band.inner.p1.y, band.inner.p2.y].map(
    (value) => value * scale,
  )

  return {
    ...rect,
    left: adjustCoverOffset(rect.left, width, rect.width, Math.min(...xValues), Math.max(...xValues)),
    top: adjustCoverOffset(rect.top, height, rect.height, Math.min(...yValues), Math.max(...yValues)),
  }
}

function adjustCoverOffset(
  offset: number,
  boxSize: number,
  displaySize: number,
  bandMin: number,
  bandMax: number,
) {
  if (displaySize <= boxSize) return offset

  const coverMin = boxSize - displaySize
  for (const margin of [6, 0]) {
    const lower = Math.max(coverMin, margin - bandMin)
    const upper = Math.min(0, boxSize - margin - bandMax)
    if (lower <= upper) return clamp(offset, lower, upper)
  }

  return clamp(boxSize / 2 - (bandMin + bandMax) / 2, coverMin, 0)
}

function damageMeta(detection: DamageDetection, index: number) {
  const type = detection.type ? `${titleCase(detection.type)} · ` : ''
  const area = typeof detection.area === 'number' && Number.isFinite(detection.area)
    ? ` · ${detection.area.toFixed(2)}% area`
    : ''
  return `${type}Issue ${index + 1}${area}`
}

function squareCropAroundBox(box: DamageBox, sourceWidth: number, sourceHeight: number): Crop {
  const width = Math.max(1, box.x2 - box.x1)
  const height = Math.max(1, box.y2 - box.y1)
  const side = Math.min(Math.max(width, height) * 2.4, Math.max(sourceWidth, sourceHeight))
  return clampCropToImage({
    x: (box.x1 + box.x2) / 2 - side / 2,
    y: (box.y1 + box.y2) / 2 - side / 2,
    width: side,
    height: side,
  }, sourceWidth, sourceHeight)
}

function normalizeDamageBox(
  box: DamageBox,
  sourceWidth: number,
  sourceHeight: number,
): DamageBox {
  const maxX = Math.max(Math.abs(box.x1), Math.abs(box.x2))
  const maxY = Math.max(Math.abs(box.y1), Math.abs(box.y2))
  if (maxX > 1.5 || maxY > 1.5) return box
  return {
    x1: box.x1 * sourceWidth,
    y1: box.y1 * sourceHeight,
    x2: box.x2 * sourceWidth,
    y2: box.y2 * sourceHeight,
  }
}

function clampCropToImage(crop: Crop, sourceWidth: number, sourceHeight: number): Crop {
  const width = Math.min(crop.width, sourceWidth)
  const height = Math.min(crop.height, sourceHeight)
  return {
    x: clamp(crop.x, 0, Math.max(0, sourceWidth - width)),
    y: clamp(crop.y, 0, Math.max(0, sourceHeight - height)),
    width,
    height,
  }
}

function titleCase(value: string) {
  return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function positive(value?: number | null) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

function outOfBounds(value?: number | null) {
  return typeof value === 'number' && value > 0 && (value < CENTERING_MIN || value > CENTERING_MAX)
}

function hasAny(values: string[], targets: string[]) {
  return targets.some((target) => values.includes(target))
}
